// /inventory/wastage — Wastage Tracking (PAGES.md §3.90)
//
// Track food wastage and raw material loss. Records by item, quantity, unit,
// reason, date, station, staff. Calculates cost via WAC. Shows trends + top
// wasted items. RESTAURANT vertical only — MANAGER+ (level 30).
// Traceability: FR-PROD-027, FR-ORD-046

import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  TextArea,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  DatePicker,
  Pagination,
  FileUpload,
  HeatmapChart,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { wastageService } from '@/services/wastage.service';
import type {
  WastageSummary,
  WastageEntry,
  WastageType,
  WastageUnit,
  WastageProductSummary,
} from '@/services/wastage.service';
import { productService } from '@/services/product.service';
import { toLocalIsoDate, todayISO, sevenDaysAgoISO } from './date-utils';
import { fetchAllWastageEntries } from './wastage-export';
import { resolvePhotoUrls, PhotoUploadError } from './wastage-photo-upload';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import {
  PlusIcon,
  TrashIcon,
  TrashEmptyIcon,
  DownloadIcon,
  CurrencyIcon,
  PercentIcon,
  FlameIcon,
  ListIcon,
} from './icons';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

/** Wastage types per FORMS.md §25.1 */
const WASTAGE_TYPE_OPTIONS: { value: WastageType; label: string }[] = [
  { value: 'SPOILAGE', label: 'Spoilage' },
  { value: 'OVERPRODUCTION', label: 'Overproduction' },
  { value: 'PREPARATION_LOSS', label: 'Preparation Loss' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'DAMAGED', label: 'Damaged' },
  { value: 'SPILLAGE', label: 'Spillage' },
  { value: 'OTHER', label: 'Other' },
];

const WASTAGE_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Types' },
  ...WASTAGE_TYPE_OPTIONS,
];

const UNIT_OPTIONS: { value: WastageUnit; label: string }[] = [
  { value: 'KG', label: 'KG' },
  { value: 'GM', label: 'GM' },
  { value: 'LTR', label: 'LTR' },
  { value: 'ML', label: 'ML' },
  { value: 'PCS', label: 'PCS' },
];

const STATION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'No Station' },
  { value: 'Grill', label: 'Grill' },
  { value: 'Bar', label: 'Bar' },
  { value: 'Tandoor', label: 'Tandoor' },
  { value: 'Dessert', label: 'Dessert' },
  { value: 'Main Kitchen', label: 'Main Kitchen' },
];

type BadgeVariant = 'danger' | 'warning' | 'pending' | 'draft' | 'active';

const TYPE_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  SPOILAGE: { label: 'Spoilage', variant: 'danger' },
  OVERPRODUCTION: { label: 'Overproduction', variant: 'warning' },
  PREPARATION_LOSS: { label: 'Prep Loss', variant: 'pending' },
  EXPIRED: { label: 'Expired', variant: 'danger' },
  DAMAGED: { label: 'Damaged', variant: 'danger' },
  SPILLAGE: { label: 'Spillage', variant: 'warning' },
  OTHER: { label: 'Other', variant: 'draft' },
};

// QA IS4-002 (owner decision ID14): an entry above the recorder's limit waits
// for approval (its stock is held, not deducted) and may be rejected.
const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  POSTED: { label: 'Posted', variant: 'active' },
  PENDING_APPROVAL: { label: 'Awaiting approval', variant: 'pending' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
};

/** Totals and analytics are MANAGER+; a cashier sees the log only. */
const EMPTY_SUMMARY: WastageSummary = {
  total_quantity: 0, total_cost: 0, wastage_percent_of_sales: 0, by_reason: [], by_product: [], by_date: [],
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Form types ────────────────────────────────────────────────────────────

interface WastageFormRow {
  key: string;
  product_id: string;
  product_name: string;
  quantity: string;
  unit: WastageUnit;
  wastage_type: WastageType;
  reason: string;
  station: string;
  /** Captured image — uploaded to S3 on submit, which yields the stored photo_url (#249). */
  photo_file: File | null;
  /** Transient object URL for the in-modal preview only. Never sent to the API (#249). */
  photo_preview: string | null;
}

const EMPTY_ROW = (): WastageFormRow => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  product_id: '',
  product_name: '',
  quantity: '',
  unit: 'KG',
  wastage_type: 'SPOILAGE',
  reason: '',
  station: '',
  photo_file: null,
  photo_preview: null,
});

interface ProductMinimal { id: string; name: string; unit?: string }

// ─── Extend for DataTable id requirement ──────────────────────────────────

interface ProductSummaryRow extends WastageProductSummary {
  id: string;
}

// ─── Mobile Wastage Card ──────────────────────────────────────────────────

function WastageMobileCard({ entry }: { entry: WastageEntry }) {
  const currency = useTenantCurrency();
  const badge = TYPE_BADGE[entry.wastage_type] ?? TYPE_BADGE.OTHER;
  const status = STATUS_BADGE[entry.status ?? 'POSTED'];
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">
            {entry.product_name}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono tabular-nums">
            {formatDate(entry.wastage_date)} · {Number(entry.quantity).toFixed(3)} {entry.unit}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
          {entry.status && entry.status !== 'POSTED' && status && (
            <Badge variant={status.variant} size="sm">{status.label}</Badge>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-sm font-bold font-mono tabular-nums text-danger-700 dark:text-danger-400">
          {formatPaise(Number(entry.cost), currency)}
        </span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          {entry.created_by_name}
        </span>
      </div>
      {entry.reason && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 line-clamp-2">
          {entry.reason}
        </p>
      )}
      {entry.photo_url && (
        <a
          href={entry.photo_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block rounded-lg overflow-hidden ring-1 ring-neutral-200 dark:ring-neutral-700"
        >
          <img
            src={entry.photo_url}
            alt={`Wastage photo — ${entry.product_name}`}
            loading="lazy"
            className="h-16 w-16 object-cover"
          />
        </a>
      )}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────

function WastageTrackingContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);
  // A cashier records wastage (QA IS4-002); the totals stay MANAGER+.
  const canSeeTotals = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER;

  // Effective location for recording wastage. The global location switcher
  // collapses to a static label (and never sets selectedLocationId) when the
  // tenant has a single location, so fall back to that location here —
  // otherwise the "Record" button would do nothing for single-location tenants.
  const effectiveLocationId = useMemo(
    () =>
      selectedLocationId ??
      (profile?.locations.length === 1 ? profile.locations[0].id : undefined),
    [selectedLocationId, profile],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Prefill captured once from the URL on first render. When arriving from an
  // Expiry Alerts "Write Off" action (issue #231), the expiring product is passed
  // via query params so the Record Wastage modal opens pre-filled with that item
  // and an EXPIRED wastage type. Captured up-front because the filter-sync effect
  // below rewrites the URL (dropping these params) shortly after mount.
  const prefillRef = useRef({
    productId: searchParams.get('product_id'),
    productName: searchParams.get('product_name') ?? '',
    wastageType: searchParams.get('wastage_type') as WastageType | null,
  });
  const prefillAppliedRef = useRef(false);

  // ── URL-synced filters ──────────────────────────────────────────────────
  const urlFrom = searchParams.get('from') ?? sevenDaysAgoISO();
  const urlTo = searchParams.get('to') ?? todayISO();
  const urlReason = searchParams.get('reason') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [startDate, setStartDate] = useState(urlFrom);
  const [endDate, setEndDate] = useState(urlTo);
  const [reasonFilter, setReasonFilter] = useState(urlReason);
  const [page, setPage] = useState(urlPage);

  // ── Sync state → URL params ─────────────────────────────────────────────
  useEffect(() => {
    const params: Record<string, string> = {};
    if (startDate !== sevenDaysAgoISO()) params.from = startDate;
    if (endDate !== todayISO()) params.to = endDate;
    if (reasonFilter) params.reason = reasonFilter;
    if (page > 1) params.page = String(page);
    setSearchParamsRef.current(params, { replace: true });
  }, [startDate, endDate, reasonFilter, page]);

  // ── Data ────────────────────────────────────────────────────────────────
  const [summary, setSummary] = useState<WastageSummary | null>(null);
  const [entries, setEntries] = useState<WastageEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // ── Modal ───────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [wastageDate, setWastageDate] = useState(todayISO());
  const [modalLocationId, setModalLocationId] = useState('');
  const [rows, setRows] = useState<WastageFormRow[]>([EMPTY_ROW()]);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [products, setProducts] = useState<ProductMinimal[]>([]);

  // Locations the user can record wastage against (from their profile).
  const locationOptions = useMemo(
    () =>
      (profile?.locations ?? [])
        .filter((l) => l.is_active !== false)
        .map((l) => ({ value: l.id, label: l.name })),
    [profile],
  );

  // ── Fetch summary + entries ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [summaryResult, listResult] = await Promise.all([
        // The type filter must scope the summary too — otherwise the KPI cards,
        // By Type breakdown and heatmap describe every type while the log below
        // shows only the filtered one (#250).
        canSeeTotals
          ? wastageService.getSummary({
            location_id: selectedLocationId ?? undefined,
            from: startDate,
            to: endDate,
            reason: reasonFilter || undefined,
          })
          : Promise.resolve(EMPTY_SUMMARY),
        wastageService.list({
          location_id: selectedLocationId ?? undefined,
          from: startDate,
          to: endDate,
          reason: reasonFilter || undefined,
          page,
          limit: PAGE_SIZE,
        }),
      ]);
      setSummary(summaryResult);
      setEntries(listResult.data);
      setTotalEntries(listResult.meta?.total ?? listResult.data.length);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load wastage data.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, startDate, endDate, reasonFilter, page, toast, canSeeTotals]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Fetch products for modal (scoped to the chosen location) ────────────
  const fetchProducts = useCallback(async (locationId?: string) => {
    try {
      const result = await productService.list({
        location_id: locationId || undefined,
        limit: 200,
      });
      setProducts(
        (result.data as unknown as ProductMinimal[]).map((p) => ({
          id: p.id,
          name: p.name,
          unit: p.unit,
        })),
      );
    } catch { /* silent */ }
  }, []);

  // Reload products whenever the modal's location changes while open.
  useEffect(() => {
    if (modalOpen) fetchProducts(modalLocationId);
  }, [modalOpen, modalLocationId, fetchProducts]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen) { e.preventDefault(); openRecordModal(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen]);

  // ── Open modal ──────────────────────────────────────────────────────────
  const openRecordModal = useCallback(() => {
    setRows([EMPTY_ROW()]);
    setWastageDate(todayISO());
    setFormError(null);
    // Default to the globally-selected location, else the user's first location.
    setModalLocationId(effectiveLocationId ?? locationOptions[0]?.value ?? '');
    setModalOpen(true);
  }, [effectiveLocationId, locationOptions]);

  // ── Prefill from Expiry Alerts "Write Off" (issue #231) ───────────────────
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const { productId, productName, wastageType } = prefillRef.current;
    if (!productId) return;
    prefillAppliedRef.current = true;
    setRows([
      {
        ...EMPTY_ROW(),
        product_id: productId,
        product_name: productName,
        wastage_type: wastageType ?? 'SPOILAGE',
      },
    ]);
    setWastageDate(todayISO());
    setFormError(null);
    setModalLocationId(effectiveLocationId ?? locationOptions[0]?.value ?? '');
    setModalOpen(true);
  }, [effectiveLocationId, locationOptions]);

  // ── Row handlers ────────────────────────────────────────────────────────
  const addRow = useCallback(() => {
    setRows((prev) => [...prev, EMPTY_ROW()]);
  }, []);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => {
      const target = prev.find((r) => r.key === key);
      if (target?.photo_preview) URL.revokeObjectURL(target.photo_preview);
      return prev.filter((r) => r.key !== key);
    });
  }, []);

  const updateRow = useCallback((key: string, field: keyof WastageFormRow, value: string | null) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
    setFormError(null);
  }, []);

  const selectProduct = useCallback((key: string, productId: string) => {
    const product = products.find((p) => p.id === productId);
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r;
      const unit = (product?.unit as WastageUnit) ?? 'KG';
      return { ...r, product_id: productId, product_name: product?.name ?? '', unit };
    }));
    setFormError(null);
  }, [products]);

  // Hold the captured File; the object URL is a preview only. The durable S3 URL
  // is obtained at submit time — sending the blob: URL to the API stored a photo
  // nobody could ever open (#249).
  const handlePhotoUpload = useCallback((key: string, files: File[]) => {
    if (!files.length) return;
    const file = files[0];
    const preview = URL.createObjectURL(file);
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r;
      if (r.photo_preview) URL.revokeObjectURL(r.photo_preview);
      return { ...r, photo_file: file, photo_preview: preview };
    }));
    setFormError(null);
  }, []);

  // Release the preview object URLs when the modal goes away — they are per-tab
  // handles, not real uploads (#249).
  const closeModal = useCallback(() => {
    rows.forEach((r) => { if (r.photo_preview) URL.revokeObjectURL(r.photo_preview); });
    setModalOpen(false);
  }, [rows]);

  // ── Validate + submit ───────────────────────────────────────────────────
  const validate = useCallback((): boolean => {
    if (rows.length === 0) { setFormError('Add at least one item'); return false; }
    for (const row of rows) {
      if (!row.product_id) { setFormError('Select a product for each row'); return false; }
      const qty = parseFloat(row.quantity);
      if (!qty || qty <= 0) { setFormError('Quantity must be greater than 0'); return false; }
      if (!row.wastage_type) { setFormError('Select a wastage type for each row'); return false; }
      if (!row.reason || row.reason.trim().length < 5) {
        setFormError('Reason must be at least 5 characters');
        return false;
      }
    }
    setFormError(null);
    return true;
  }, [rows]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    if (!modalLocationId) {
      setFormError('Select a location before recording wastage.');
      return;
    }
    setIsSaving(true);
    try {
      // Upload captured photos first — the API stores the durable S3 URL these
      // return. A failure aborts the whole submission rather than silently
      // recording wastage without the evidence the user attached (#249).
      const photoUrls = await resolvePhotoUrls(rows.map((r) => r.photo_file));

      const created = await wastageService.create({
        location_id: modalLocationId,
        items: rows.map((r, idx) => ({
          product_id: r.product_id,
          quantity: parseFloat(r.quantity),
          unit: r.unit,
          wastage_type: r.wastage_type,
          reason: r.reason.trim(),
          wastage_date: wastageDate,
          station: r.station || undefined,
          photo_url: photoUrls[idx],
        })),
      });
      if (created.status === 'PENDING_APPROVAL') {
        // Say why it waits: the entry is above the recorder's wastage limit.
        toast({
          title: created.limit != null
            ? `Sent for approval — ${formatPaise(created.amount ?? 0, currency)} is above your wastage limit of ${formatPaise(created.limit, currency)}. The stock is held until it is approved.`
            : 'Sent for approval — the stock is held until it is approved.',
          variant: 'warning',
        });
      } else {
        toast({
          title: `${rows.length} wastage item${rows.length !== 1 ? 's' : ''} recorded`,
          variant: 'success',
        });
      }
      closeModal();
      fetchData();
    } catch (err) {
      if (err instanceof PhotoUploadError) {
        toast({ title: 'Photo upload failed — nothing was recorded. Please try again.', variant: 'error' });
      } else if (err instanceof ApiError && err.message.includes('not found')) {
        toast({ title: 'Item not found', variant: 'error' });
      } else if (err instanceof ApiError && err.message.includes('insufficient stock')) {
        toast({ title: err.message, variant: 'error' });
      } else {
        toast({ title: err instanceof ApiError ? err.message : 'Failed to record wastage.', variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  }, [validate, modalLocationId, rows, wastageDate, fetchData, closeModal, toast, currency]);

  // ── Export CSV ──────────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (!entries.length) return;
    let all: WastageEntry[];
    try {
      all = await fetchAllWastageEntries((p, limit) =>
        wastageService.list({
          location_id: selectedLocationId ?? undefined,
          from: startDate,
          to: endDate,
          reason: reasonFilter || undefined,
          page: p,
          limit,
        }),
      );
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Export failed. Please try again.', variant: 'error' });
      return;
    }
    const header = `Date,Item,Qty,Unit,Type,Cost (${currency}),Recorded By,Reason\n`;
    const csvRows = all.map((e) =>
      `"${e.wastage_date}","${e.product_name}",${Number(e.quantity).toFixed(3)},"${e.unit}","${e.wastage_type}",${(Number(e.cost) / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency))},"${e.created_by_name}","${(e.reason ?? '').replace(/"/g, '""')}"`,
    ).join('\n');
    const blob = new Blob([header + csvRows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wastage-report-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'CSV exported', variant: 'success' });
  }, [entries, selectedLocationId, startDate, endDate, reasonFilter, toast, currency]);

  // ── Derived data ────────────────────────────────────────────────────────
  const totalCost = summary?.total_cost ?? 0;
  const wastagePercent = summary?.wastage_percent_of_sales ?? 0;
  const topProduct = summary?.by_product[0]?.product_name ?? '—';
  const totalRecords = totalEntries;

  // Product summary rows for heatmap / breakdown
  const productSummaryRows: ProductSummaryRow[] = useMemo(() => {
    return (summary?.by_product ?? []).map((p) => ({ ...p, id: p.product_id }));
  }, [summary]);

  // Reason breakdown
  const reasonBreakdown = useMemo(() => {
    return (summary?.by_reason ?? []).map((r) => ({
      reason: r.reason,
      badge: TYPE_BADGE[r.reason] ?? TYPE_BADGE.OTHER,
      count: r.item_count,
      cost: Number(r.total_cost),
    }));
  }, [summary]);

  // Heatmap data
  const heatmapData = useMemo(() => {
    if (!summary?.by_date.length) return [];
    return summary.by_date.map((d) => ({
      row: d.date,
      col: 'Wastage Cost',
      value: Number(d.total_cost) / getSubunitMultiplier(currency),
    }));
  }, [summary]);

  // ── Wastage log columns (spec: Date, Item, Qty, Unit, Type, Reason, Station, Cost, Recorded By) ──
  const logColumns: ColumnDef<WastageEntry>[] = useMemo(() => [
    {
      key: 'wastage_date', header: 'Date',
      render: (_v: unknown, row: WastageEntry) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300 font-mono tabular-nums">
          {formatDate(row.wastage_date)}
        </span>
      ),
    },
    {
      key: 'product_name', header: 'Item',
      render: (_v: unknown, row: WastageEntry) => (
        <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.product_name}</span>
      ),
    },
    {
      key: 'quantity', header: 'Qty', numeric: true,
      render: (_v: unknown, row: WastageEntry) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {Number(row.quantity).toFixed(3)}
        </span>
      ),
    },
    {
      key: 'unit', header: 'Unit',
      render: (_v: unknown, row: WastageEntry) => (
        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{row.unit}</span>
      ),
    },
    {
      key: 'wastage_type', header: 'Type',
      render: (_v: unknown, row: WastageEntry) => {
        const badge = TYPE_BADGE[row.wastage_type] ?? TYPE_BADGE.OTHER;
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
    {
      key: 'reason', header: 'Reason',
      render: (_v: unknown, row: WastageEntry) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.reason || '—'}</span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: WastageEntry) => {
        const status = STATUS_BADGE[row.status ?? 'POSTED'] ?? STATUS_BADGE.POSTED;
        return <Badge variant={status.variant} size="sm">{status.label}</Badge>;
      },
    },
    {
      key: 'station', header: 'Station',
      render: (_v: unknown, row: WastageEntry) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.station ?? '—'}</span>
      ),
    },
    {
      key: 'cost', header: 'Cost', numeric: true,
      render: (_v: unknown, row: WastageEntry) => (
        <span className="font-mono tabular-nums font-bold text-danger-700 dark:text-danger-400">
          {formatPaise(Number(row.cost), currency)}
        </span>
      ),
    },
    {
      key: 'created_by_name', header: 'Recorded By',
      render: (_v: unknown, row: WastageEntry) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.created_by_name}</span>
      ),
    },
    {
      key: 'photo_url', header: 'Photo',
      render: (_v: unknown, row: WastageEntry) => (
        row.photo_url ? (
          <a
            href={row.photo_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-lg overflow-hidden ring-1 ring-neutral-200 dark:ring-neutral-700 hover:ring-2 hover:ring-brand-400 transition-all duration-200"
          >
            <img
              src={row.photo_url}
              alt={`Wastage photo — ${row.product_name}`}
              loading="lazy"
              className="h-10 w-10 object-cover"
            />
          </a>
        ) : (
          <span className="text-xs text-neutral-400 dark:text-neutral-600">—</span>
        )
      ),
    },
  ], [currency]);

  // ── Product summary columns ─────────────────────────────────────────────
  const productColumns: ColumnDef<ProductSummaryRow>[] = useMemo(() => [
    {
      key: 'product_name', header: 'Product',
      render: (_v: unknown, row: ProductSummaryRow) => (
        <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.product_name}</span>
      ),
    },
    {
      key: 'item_count', header: 'Entries', numeric: true,
      render: (_v: unknown, row: ProductSummaryRow) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{row.item_count}</span>
      ),
    },
    {
      key: 'total_quantity', header: 'Total Qty', numeric: true,
      render: (_v: unknown, row: ProductSummaryRow) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {Number(row.total_quantity).toFixed(3)}
        </span>
      ),
    },
    {
      key: 'total_cost', header: 'Total Cost', numeric: true,
      render: (_v: unknown, row: ProductSummaryRow) => (
        <span className="font-mono tabular-nums font-bold text-danger-700 dark:text-danger-400">
          {formatPaise(Number(row.total_cost), currency)}
        </span>
      ),
    },
  ], [currency]);

  // ── Product options for form picker ─────────────────────────────────────
  const productOptions = useMemo(() =>
    products.map((p) => ({ value: p.id, label: p.name })),
  [products]);

  const totalPages = Math.ceil(totalEntries / PAGE_SIZE);

  return (
    <PageContainer
      title="Wastage Tracking"
      subtitle="Track food wastage and raw material loss"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Inventory', href: '/inventory' },
        { label: 'Wastage' },
      ]}
      actions={
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={handleExport} disabled={!entries.length}>
            <DownloadIcon /><span className="ml-1.5">Export CSV</span>
          </Button>
          {/* Hidden on mobile — FAB used instead */}
          <div className="hidden sm:block">
            <Button variant="primary" size="sm" onClick={openRecordModal}>
              <PlusIcon /><span className="ml-1.5">Record Wastage</span>
            </Button>
          </div>
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ Date + Reason Filters ═══ */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">From</span>
              <DatePicker
                value={startDate ? new Date(startDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => { setStartDate(toLocalIsoDate(d)); setPage(1); }}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">To</span>
              <DatePicker
                value={endDate ? new Date(endDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => { setEndDate(toLocalIsoDate(d)); setPage(1); }}
              />
            </div>
            <div className="w-44 ml-auto">
              <Select
                options={WASTAGE_TYPE_FILTER_OPTIONS}
                value={reasonFilter}
                onChange={(val) => {
                  setReasonFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All Types"
                clearable
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            {/* Skeleton stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <StatCard title="" value="" loading />
                </div>
              ))}
            </div>
            {/* Skeleton chart */}
            <div className={cn(GLASS, 'p-6')}>
              <Skeleton variant="rect" width="100%" height={200} />
            </div>
            {/* Skeleton table */}
            <div className={cn(GLASS, 'p-6 space-y-4')}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="text" width="8%" height={18} />
                  <Skeleton variant="rect" width={70} height={24} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                </div>
              ))}
            </div>
          </div>
        ) : !summary || (entries.length === 0 && summary.by_product.length === 0) ? (
          <EmptyState
            variant="page"
            icon={<TrashEmptyIcon />}
            title={reasonFilter ? 'No wastage of this type' : 'No wastage records'}
            description={
              reasonFilter
                ? 'No entries match the selected type in this date range'
                : 'Record wastage to track food loss and reduce costs'
            }
            action={{ label: 'Record Wastage', onClick: openRecordModal }}
          />
        ) : (
          <>
            {/* ═══ KPI Stat Cards (spec: 4 cards) — MANAGER+ ═══ */}
            {canSeeTotals && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="animate-slide-up" aria-live="polite">
                <StatCard
                  title="Total Wastage Cost"
                  value={formatPaise(totalCost, currency)}
                  icon={<CurrencyIcon className="h-4 w-4" />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
                <StatCard
                  title="Wastage % of Sales"
                  value={`${wastagePercent.toFixed(1)}%`}
                  icon={<PercentIcon className="h-4 w-4" />}
                  variant={wastagePercent > 5 ? 'trend' : 'default'}
                  trend={wastagePercent > 5 ? { direction: 'up', value: `${wastagePercent.toFixed(1)}%`, positive: false } : undefined}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                <StatCard
                  title="Top Wasted Item"
                  value={topProduct}
                  icon={<FlameIcon className="h-4 w-4" />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
                <StatCard
                  title="Total Entries"
                  value={String(totalRecords)}
                  icon={<ListIcon className="h-4 w-4" />}
                />
              </div>
            </div>}

            {/* ═══ Reason Breakdown ═══ */}
            {reasonBreakdown.length > 0 && (
              <div className={cn(GLASS, 'p-4 mb-6')}>
                <h3 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                  By Type
                </h3>
                <div className="flex flex-wrap gap-3">
                  {reasonBreakdown.map((r) => (
                    <div key={r.reason} className={cn(GLASS_INTERACTIVE, 'flex items-center gap-2 px-3 py-2')}>
                      <Badge variant={r.badge.variant} size="sm">{r.badge.label}</Badge>
                      <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
                        {r.count} entries
                      </span>
                      <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                        {formatPaise(r.cost, currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ Heatmap ═══ */}
            {heatmapData.length > 0 && (
              <div className={cn(GLASS, 'p-4 mb-6')}>
                <HeatmapChart
                  data={heatmapData}
                  title="Wastage Trend"
                  rowLabel="Date"
                  colLabel=""
                />
              </div>
            )}

            {/* ═══ Wastage Log — Desktop DataTable ═══ */}
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Wastage Log ({totalEntries})
                </h3>
              </div>
              <DataTable<WastageEntry> columns={logColumns} data={entries} />
            </div>

            {/* ═══ Wastage Log — Mobile Card List ═══ */}
            <div className="block sm:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Wastage Log ({totalEntries})
              </h3>
              {entries.length === 0 ? (
                <div className={cn(GLASS, 'p-12')}>
                  <EmptyState
                    variant="inline"
                    icon={<TrashEmptyIcon />}
                    title="No entries"
                    description="No wastage records match your filters"
                  />
                </div>
              ) : (
                entries.map((entry) => (
                  <WastageMobileCard key={entry.id} entry={entry} />
                ))
              )}
            </div>

            {/* ═══ Pagination ═══ */}
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalEntries}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}

            {/* ═══ Product Summary Table ═══ */}
            {productSummaryRows.length > 0 && (
              <div className={cn(GLASS, 'overflow-hidden')}>
                <div className="px-5 py-4">
                  <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Wastage by Product ({productSummaryRows.length})
                  </h3>
                </div>
                <DataTable<ProductSummaryRow> columns={productColumns} data={productSummaryRows} />
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ Mobile FAB ═══ */}
      <button
        type="button"
        onClick={openRecordModal}
        className="fixed bottom-6 right-6 sm:hidden z-40 w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-danger-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center active:scale-95"
        aria-label="Record Wastage"
      >
        <PlusIcon />
      </button>

      {/* ═══ Record Wastage Modal ═══ */}
      <Modal open={modalOpen} onClose={closeModal} title="Record Wastage" variant="wide">
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg">
              {formError}
            </div>
          )}

          {/* Location + Wastage date (shared across all rows) */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Location <span className="text-danger-500">*</span>
              </label>
              <Select
                options={locationOptions}
                value={modalLocationId}
                onChange={(v) => setModalLocationId(typeof v === 'string' ? v : v[0] ?? '')}
                placeholder="Select location"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Wastage Date
              </label>
              <DatePicker
                value={wastageDate ? new Date(wastageDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => setWastageDate(toLocalIsoDate(d))}
              />
            </div>
          </div>

          <div className="space-y-3">
            {rows.map((row, _idx) => (
              <div
                key={row.key}
                className={cn(GLASS, 'p-4 space-y-3')}
              >
                {/* Row 1: Product + Quantity + Unit */}
                <div className="flex flex-wrap items-start gap-2">
                  <div className="flex-1 min-w-[180px]">
                    <Select
                      options={productOptions}
                      value={row.product_id}
                      onChange={(val) => selectProduct(row.key, typeof val === 'string' ? val : val[0] ?? '')}
                      placeholder="Select item"
                    />
                  </div>
                  <div className="w-24">
                    <Input
                      value={row.quantity}
                      onChange={(val) => updateRow(row.key, 'quantity', val)}
                      type="number"
                      placeholder="Qty"
                      aria-label="Quantity"
                    />
                  </div>
                  <div className="w-24">
                    <Select
                      options={UNIT_OPTIONS}
                      value={row.unit}
                      onChange={(val) => updateRow(row.key, 'unit', typeof val === 'string' ? val : val[0] ?? 'KG')}
                    />
                  </div>
                </div>

                {/* Row 2: Wastage Type + Station */}
                <div className="flex flex-wrap items-start gap-2">
                  <div className="w-48">
                    <Select
                      options={WASTAGE_TYPE_OPTIONS}
                      value={row.wastage_type}
                      onChange={(val) => updateRow(row.key, 'wastage_type', typeof val === 'string' ? val : val[0] ?? 'SPOILAGE')}
                    />
                  </div>
                  <div className="w-44">
                    <Select
                      options={STATION_OPTIONS}
                      value={row.station}
                      onChange={(val) => updateRow(row.key, 'station', typeof val === 'string' ? val : val[0] ?? '')}
                      placeholder="Station (optional)"
                      aria-describedby="station-help"
                    />
                  </div>
                  <span id="station-help" className="sr-only">
                    Kitchen station where the wastage occurred
                  </span>
                </div>

                {/* Row 3: Reason (required TextArea, min 5 chars) */}
                <div>
                  <TextArea
                    value={row.reason}
                    onChange={(val) => updateRow(row.key, 'reason', val)}
                    placeholder="Reason for wastage (min 5 characters)"
                    rows={2}
                    maxLength={500}
                    required
                    aria-label="Wastage reason"
                  />
                </div>

                {/* Row 4: Photo upload (optional) + remove */}
                <div className="flex items-end justify-between gap-3">
                  <div className="flex-1 max-w-xs">
                    <FileUpload
                      variant="camera"
                      accept="image/*"
                      maxSize={5 * 1024 * 1024}
                      onUpload={(files) => handlePhotoUpload(row.key, files)}
                      preview={row.photo_preview}
                      aria-label="Capture photo of wasted item"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRow(row.key)}
                    disabled={rows.length <= 1}
                    className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20 shrink-0"
                    aria-label="Remove row"
                  >
                    <TrashIcon />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button variant="ghost" size="sm" onClick={addRow} aria-label="Add item">
            <PlusIcon /><span className="ml-1">Add Item</span>
          </Button>

          <div className="flex justify-end gap-3 pt-2 border-t border-neutral-100 dark:border-neutral-700/50">
            <Button variant="ghost" onClick={closeModal} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? 'Recording...' : `Record ${rows.length} Item${rows.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function WastageTrackingPage() {
  return <ErrorBoundary><WastageTrackingContent /></ErrorBoundary>;
}
